import { hubApi } from '@/app/api/hubApi';
import { backendLanguage } from '@/lib/backendLanguage';
import type {
  AvailabilityResponse,
  ConfirmedReservation,
  CreateReservationInput,
} from '@/types';

// Raw shapes from /api/reservations/availability/
interface RawAvailabilitySlot {
  time: string;
  max_covers: number;
  remaining: number;
  available: boolean;
}

interface RawAvailabilityResponse {
  restaurant_uuid: string;
  date: string;
  slots: RawAvailabilitySlot[];
}

// Raw shape from POST /api/reservations/ 201 response
interface RawConfirmedReservation {
  uuid: string;
  status: 'CONFIRMED';
  reserved_for: string;
  party_size: number;
  customer_name: string;
  restaurant_uuid: string;
  created_at: string;
}

function transformAvailability(raw: RawAvailabilityResponse): AvailabilityResponse {
  return {
    restaurantUuid: raw.restaurant_uuid,
    date: raw.date,
    slots: raw.slots.map((s) => ({
      time: s.time,
      maxCovers: s.max_covers,
      remaining: s.remaining,
      available: s.available,
    })),
  };
}

function transformConfirmed(raw: RawConfirmedReservation): ConfirmedReservation {
  return {
    uuid: raw.uuid,
    status: raw.status,
    reservedFor: raw.reserved_for,
    partySize: raw.party_size,
    customerName: raw.customer_name,
    restaurantUuid: raw.restaurant_uuid,
    createdAt: raw.created_at,
  };
}

export const reservationApi = hubApi.injectEndpoints({
  endpoints: (build) => ({
    getAvailability: build.query<
      AvailabilityResponse,
      { restaurantUuid: string; date: string }
    >({
      query: ({ restaurantUuid, date }) =>
        `/reservations/availability/?restaurant_uuid=${encodeURIComponent(restaurantUuid)}&date=${encodeURIComponent(date)}`,
      transformResponse: transformAvailability,
    }),

    createReservation: build.mutation<ConfirmedReservation, CreateReservationInput>({
      query: (input) => ({
        url: '/reservations/',
        method: 'POST',
        body: {
          restaurant_uuid: input.restaurantUuid,
          customer_name: input.customerName,
          customer_phone: input.customerPhone,
          customer_email: input.customerEmail,
          party_size: input.partySize,
          reserved_for: input.reservedFor,
          notes: input.notes,
          language: backendLanguage(input.language),
          recaptcha_token: input.recaptchaToken ?? '',
        },
      }),
      transformResponse: transformConfirmed,
    }),
  }),
});

export const {
  useGetAvailabilityQuery,
  useCreateReservationMutation,
} = reservationApi;
